import React, { useMemo } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, useMediaQuery, useTheme, IconButton } from '@mui/material';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SIDEBAR_STATE } from '../../../store/actions';

// import EvidenceIcon from '@mui/icons-material/ManageSearch';
// import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
// import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
// import StoreMallDirectoryOutlinedIcon from '@mui/icons-material/StoreMallDirectoryOutlined';
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
// import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
// import EvidenceIcon from './evidenceIcon';

const menuItems = [
  {
    id: 'storeoverview',
    title: 'Overview',
    type: 'item',
    url: '/main/stores/storeinsight',
    icon: ImportContactsOutlinedIcon
  },
  {
    id: 'videos',
    title: 'Videos',
    type: 'item',
    url: '/main/stores/storevideo',
    icon: OndemandVideoIcon
  },
  // {
  //   id: 'evidences',
  //   title: 'Evidence',
  //   type: 'item',
  //   url: '/main/stores/evidence',
  //   icon: EvidenceIcon
  // }
  // {
  //   id: 'teams',
  //   title: 'Team',
  //   type: 'item',
  //   url: '/main/stores/team',
  //   icon: PeopleAltOutlinedIcon
  // },
  // {
  //   id: 'Stores',
  //   title: 'Store',
  //   type: 'item',
  //   url: '/main/stores/store',
  //   icon: StoreMallDirectoryOutlinedIcon
  // }
  // {
  //   id: 'notifications',
  //   title: 'Notification',
  //   type: 'item',
  //   url: '/main/stores/notification',
  //   icon: NotificationsActiveOutlinedIcon
  // },
];

const SideBar = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExpanded = useSelector((state) => state.customization.isExpanded);

  const handleToggle = () => {
    dispatch({ type: SIDEBAR_STATE, isExpanded: !isExpanded });
  };

  const sidebarStyles = useMemo(
    () => ({
      root: {
        width: isExpanded ? (isMobile ? '250px' : '250px') : isMobile ? '60px' : '40px',
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        height: 'calc(100vh - 65px)',
        position: 'fixed',
        top: '65px',
        left: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.3s ease',
        boxShadow: theme.shadows[1],
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        paddingRight: '50px'
      },
      toggleContainer: {
        position: 'relative',
        width: '100%',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '0 8px'
      },
      toggleButton: {
        position: 'absolute',
        left: '8px',
        top: '8px',
        padding: '8px',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark
        },
        minWidth: '32px',
        height: '32px',
        borderRadius: '50%'
      },
      list: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        flex: 1
      },
      listItem: {
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5),
        whiteSpace: 'nowrap',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        width: 'auto',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          '& .MuiListItemIcon-root': {
            color: '#000 !important'
          }
        }
      },
      listItemActive: {
        backgroundColor: isExpanded ? theme.palette.primary.main : 'transparent',
        color: isExpanded ? theme.palette.primary.contrastText : 'inherit',
        '&:hover': {
          backgroundColor: isExpanded ? theme.palette.primary.dark : 'rgba(0, 0, 0, 0.1)', // Light black for hover on active item too
          '& .MuiListItemText-primary': {
            color: isExpanded ? theme.palette.primary.contrastText : 'inherit' // Keep title color consistent when active
          }
        },
        '& .MuiListItemIcon-root': {
          color: theme.palette.primary.main
        }
      },

      icon: {
        minWidth: '40px',
        color: 'inherit',
        marginRight: theme.spacing(2)
      },
      footer: {
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        textAlign: 'center',
        marginTop: 'auto',
        opacity: isExpanded ? 1 : 0,
        transition: 'opacity 0.3s ease',
        visibility: isExpanded ? 'visible' : 'hidden'
      }
    }),
    [theme, isMobile, isExpanded]
  );

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isPathActive = (path) => {
    return pathname === path || (pathname.startsWith(path) && pathname.charAt(path.length) === '/');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={sidebarStyles.root}>
        <Box sx={sidebarStyles.toggleContainer}>
          <IconButton onClick={handleToggle} sx={sidebarStyles.toggleButton} size="small">
            {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
        <List component="nav" sx={sidebarStyles.list}>
          {menuItems.map((item) => {
            const isActive = isPathActive(item.url);
            const IconComponent = item.icon;

            return (
              <ListItem
                key={item.id}
                onClick={() => handleNavigation(item.url)}
                sx={{
                  ...sidebarStyles.listItem,
                  ...(isActive && sidebarStyles.listItemActive),
                  cursor: 'pointer',
                  justifyContent: isExpanded ? 'flex-start' : 'center'
                }}
              >
                <ListItemIcon sx={{ ...sidebarStyles.icon, marginRight: isExpanded ? theme.spacing(2) : -1.1, color: '#000 !important' }}>
                  {item.id === 'evidences' ? <IconComponent sx={{ fontSize: 25 }} /> : <IconComponent />}
                </ListItemIcon>

                {isExpanded && <ListItemText primary={item.title} />}
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={sidebarStyles.content}>{/* Your content goes here */}</Box>
    </Box>
  );
};

export default SideBar;